/**
 * Soft-safe SoftGate UI + intent runners (arch Move 2).
 * Phases: offer → confirm-force → ack runs intent.
 * Fix navigates to the gap target (cards if holes missing, else actions).
 *
 * Soft-refuse: no GTO / equity% / outs / FAQ strategy fill / empty-layer
 * fabrication. Soft 14 ops-only — no crawl locale / FAQ fill here.
 * Soft-safe ①: shareViewModel remains the single human face for UI|Copy|PNG;
 * this module only gates incomplete hands — it never builds share payloads.
 * Soft C1 Soft-safe Quiet: L1.0 holes-only (holes present, no actions) bypasses
 * SoftGate via handLooksIncomplete — no double incomplete-action confirm.
 * Soft C SoftGate body sync: when SoftGate opens (holes gap), body/copy speak
 * only to that gate condition — do not co-list action-incomplete as SoftGate
 * pressure (virtual review D3). SoftGate chrome must never enter Copy / PNG /
 * OG / share VM (Soft-safe ③ / Soft 8). Soft C1 Soft 6: SoftGate panel + Fix
 * CTA Quiet weight < L1 .shareCard — inset quiet chrome, never btnPrimary.
 */

import { t } from '../i18n/strings'
import { markImageSaved } from './savedMarker'
import { formatEndingLine } from '../lib/endingRecord'
import { buildHandShareUrl } from '../lib/handLink'
import { buildHandImageBlob } from '../lib/imageExport'
import { boardMismatchWarnings, formatSeatCode, getPositions, handLineText, handShareMetaHead } from '../lib/poker'
import { buildCopyPayload, type CopyMode } from '../lib/shareCopy'
import { shareProcessKind, shareProcessLineText } from '../lib/potShape'
import { formatShareReviewedLine } from '../lib/shareStamp'
import {
  copyTextToClipboard,
  downloadPngBlob,
  shareOrCopyText,
  sharePngFile,
} from '../lib/shareText'
import {
  acknowledgeSoftGate,
  armSoftGateForce,
  dismissSoftGate,
  getState,
  handLooksIncomplete,
  incompleteGaps,
  setCaptureMode,
  setStep,
  showToast,
  type SoftGateIntent,
} from '../state'
import { el } from './dom'

/** Intent runners after SoftGate ack (complete-hand paths call these too). */
export async function runCopySummary(mode: CopyMode = 'both'): Promise<void> {
  const text = buildCopyPayload(getState().hand, mode)
  const ok = await copyTextToClipboard(text)
  if (ok) showToast('copied')
  else showToast('shareFailed')
}

export async function runShareText(): Promise<void> {
  const text = buildCopyPayload(getState().hand, 'both')
  const result = await shareOrCopyText(text)
  if (result === 'shared') showToast('shared')
  else if (result === 'copied') showToast('copied')
  else if (result === 'failed') showToast('shareFailed')
  // Soft residual 6: aborted/cancel — no success toast (OS sheet variance)
}

function imageExportLabelsFor(handIncomplete: boolean) {
  const ui = t()
  const { hand } = getState()
  const kind = shareProcessKind(hand, getPositions(hand.playerCount))
  const endingLine = formatEndingLine(hand, ui)
  return {
    brand: ui.brand,
    footer: ui.summaryFooter,
    holesHeader: ui.summaryHole,
    boardHeader: ui.summaryBoard,
    actionsHeader: ui.summaryActions,
    // Softwise M10b: same single line text as Share card / input strip.
    lineText: handLineText(hand),
    stackChipsUnit: ui.stackChipsUnit,
    // Softwise M10d: same settings line as the Share card.
    metaHead: handShareMetaHead(hand),
    takeawayHeader: ui.takeawayLabel,
    // Soft G: optional ending line (absent when empty → identical labels).
    ...(endingLine ? { endingLine } : {}),
    // Soft Quiet C1: denser street chronology is UI-only — not PNG labels.
    ...(handIncomplete
      ? { incompleteBadge: ui.incompleteBadge }
      : {
          processLine: shareProcessLineText(kind, ui) ?? undefined,
          // Stamp at export time so PNG datetime matches the save/share moment
          reviewedLine: formatShareReviewedLine(ui.shareReviewedLabel),
        }),
  }
}

export async function runSaveShareImage(): Promise<void> {
  const { hand } = getState()
  try {
    const blob = await buildHandImageBlob(
      hand,
      imageExportLabelsFor(handLooksIncomplete(hand)),
    )
    await downloadPngBlob(blob, 'holecard-ex-hand.png')
    markImageSaved()
    showToast('imageSaved')
  } catch {
    showToast('imageFailed')
  }
}

export async function runShareShareImage(): Promise<void> {
  const ui = t()
  const { hand } = getState()
  try {
    const blob = await buildHandImageBlob(
      hand,
      imageExportLabelsFor(handLooksIncomplete(hand)),
    )
    const result = await sharePngFile(blob, 'holecard-ex-hand.png', ui.brand)
    if (result === 'shared') showToast('imageShared')
    else if (result === 'unsupported') {
      await downloadPngBlob(blob, 'holecard-ex-hand.png')
      markImageSaved()
      showToast('imageSaved')
    } else if (result === 'failed') showToast('imageFailed')
    // Soft residual 6: aborted/cancel — no success toast (OS sheet variance)
  } catch {
    showToast('imageFailed')
  }
}

export async function runCopyHandLink(): Promise<void> {
  const result = buildHandShareUrl(getState().hand)
  if (!result.ok) {
    showToast('linkTooLong')
    return
  }
  const ok = await copyTextToClipboard(result.url)
  if (!ok) {
    showToast('shareFailed')
    return
  }
  // Never toast plain success when villain knownHoles were dropped for length.
  // Soft G: same honesty when the optional ending was left out of the link.
  showToast(
    result.strippedKnownHoles
      ? 'linkWithoutKnownHoles'
      : result.strippedTakeaway
        ? 'linkWithoutTakeaway'
        : result.strippedEnding
          ? 'linkWithoutEnding'
          : 'linkCopied',
  )
}

function buildSoftGateBodyText(): string {
  const ui = t()
  const { hand } = getState()
  const gaps = incompleteGaps(hand)
  // Soft C SoftGate body sync: SoftGate opens for holes gap only (Soft C1).
  // Named body lists that gate condition alone — never co-list actions as
  // SoftGate pressure (D3 double-pressure). incompleteGaps.actions stays for
  // Fix→gap routing inventory; SoftGate chrome does not surface it here.
  let body = gaps.holes
    ? // Soft F: seat name (BTN etc.), never `Hero`.
      `${ui.softGateBodyLead} ${ui.softGateMissingHoles(formatSeatCode(hand.heroPosition, hand.playerCount))}`
    : ui.softGateBody
  const boardWarns = boardMismatchWarnings(hand)
  if (boardWarns.length > 0) {
    body += `\n${ui.boardActionsMismatch}: ${boardWarns.join(' · ')}`
  }
  return body
}

function softGateAnywayLabel(
  intent: SoftGateIntent,
  phase: 'offer' | 'confirm-force',
): string {
  const ui = t()
  if (phase === 'confirm-force') return ui.softGateForceConfirm
  switch (intent) {
    case 'copy':
    case 'link':
      return ui.softGateCopy
    case 'share':
    case 'png-save':
    case 'png-share':
      return ui.softGateShare
    case 'summary':
    case 'capture':
    default:
      return ui.softGateContinue
  }
}

/** Fix: dismiss gate and jump to the gap (holes → cards, else actions). */
function onSoftGateFix(): void {
  const { hand } = getState()
  const gaps = incompleteGaps(hand)
  dismissSoftGate()
  setStep(gaps.holes ? 'cards' : 'actions')
}

/**
 * Ack after confirm-force: run the gated intent.
 * Intents: summary | share | copy | png-save | png-share | capture | link.
 */
async function onSoftGateAck(): Promise<void> {
  const gate = acknowledgeSoftGate()
  if (!gate) return
  if (gate.intent === 'summary') {
    setStep('summary')
    return
  }
  if (gate.intent === 'share') {
    await runShareText()
    return
  }
  if (gate.intent === 'copy') {
    await runCopySummary(gate.copyMode ?? 'both')
    return
  }
  if (gate.intent === 'png-save') {
    await runSaveShareImage()
    return
  }
  if (gate.intent === 'png-share') {
    await runShareShareImage()
    return
  }
  if (gate.intent === 'capture') {
    setCaptureMode(true)
    return
  }
  if (gate.intent === 'link') {
    await runCopyHandLink()
  }
}

const SOFT_GATE_FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function attachSoftGateA11y(
  root: HTMLElement,
  primaryBtn: HTMLButtonElement,
): void {
  requestAnimationFrame(() => {
    primaryBtn.focus()
  })
  root.addEventListener('keydown', (ev: KeyboardEvent) => {
    if (ev.key === 'Escape') {
      ev.preventDefault()
      ev.stopPropagation()
      dismissSoftGate()
      return
    }
    if (ev.key !== 'Tab') return
    const focusables = Array.from(
      root.querySelectorAll<HTMLElement>(SOFT_GATE_FOCUSABLE),
    ).filter((n) => n.offsetParent !== null || n === document.activeElement)
    if (focusables.length === 0) return
    const first = focusables[0]!
    const last = focusables[focusables.length - 1]!
    if (ev.shiftKey && document.activeElement === first) {
      ev.preventDefault()
      last.focus()
    } else if (!ev.shiftKey && document.activeElement === last) {
      ev.preventDefault()
      first.focus()
    }
  })
}

/**
 * SoftGate alertdialog. Secondary: offer → arm confirm-force; confirm-force → ack.
 * Shell-only — Summary must not mount a second alertdialog.
 */
export function softGateBanner(): HTMLElement | null {
  const ui = t()
  const { softGate } = getState()
  if (!softGate) return null
  const phase = softGate.phase === 'confirm-force' ? 'confirm-force' : 'offer'
  let body = buildSoftGateBodyText()
  // Challenging Soft-safe: confirm-force restates incompleteness so the
  // second click is an honest force, not a quiet chrome flip.
  if (phase === 'confirm-force') {
    body = `${ui.softGateForceConfirm}\n${body}`
  }
  const secondaryLabel = softGateAnywayLabel(softGate.intent, phase)

  // Soft C1 Soft 6: Fix CTA Quiet — never btnPrimary white competing with L1 .shareCard
  const primaryBtn = el('button', {
    className: 'btn btnQuiet softGateFixBtn',
    attrs: { type: 'button' },
    text: ui.softGateFix,
    on: { click: () => onSoftGateFix() },
  })
  const secondaryBtn = el('button', {
    className: 'btn',
    attrs: { type: 'button' },
    text: secondaryLabel,
    on: {
      click: () => {
        if (phase === 'offer') armSoftGateForce()
        else void onSoftGateAck()
      },
    },
  })
  const cancelBtn = el('button', {
    className: 'btn btnGhost',
    attrs: { type: 'button' },
    text: ui.softGateCancel,
    on: { click: () => dismissSoftGate() },
  })

  const root = el(
    'div',
    {
      className: 'softGate',
      attrs: {
        role: 'alertdialog',
        'aria-modal': 'true',
        'aria-labelledby': 'soft-gate-title',
        'aria-describedby': 'soft-gate-body',
      },
    },
    [
      el('div', {
        className: 'softGateTitle',
        attrs: { id: 'soft-gate-title' },
        text: ui.softGateTitle,
      }),
      el('p', {
        className: 'softGateBody',
        attrs: { id: 'soft-gate-body' },
        text: body,
      }),
      el('div', { className: 'softGateActions' }, [
        primaryBtn,
        secondaryBtn,
      ]),
      cancelBtn,
    ],
  )
  attachSoftGateA11y(root, primaryBtn)
  return root
}
